import { apiGet, apiPost } from '../../../adapters/api-client/api';

/**
 * UC-A09 — teacher supply health and coverage.
 *
 * Every payload here is a projection of records other contexts own. The
 * console reads it and links back; it never stores a copy.
 */

export interface TeacherRatingView {
  totalReviews: number;
  averageRating: number;
  floor: number;
  /** `rating_floor@2`, or `rating_floor@default` while the default governs. */
  floorVersion: string;
  belowFloor: boolean;
}

export interface TeacherHealthSummary {
  teacherId: string;
  name: string;
  /** As the user record stores it. Never a recomputed eligibility. */
  verificationStatus: string | null;
  applicationState: string | null;
  publishedCourses: number;
  activeEnrollments: number;
  rating: TeacherRatingView;
}

export type TeacherIncidentKind = 'TEACHER_CANCELLATION' | 'TEACHER_NO_SHOW';

export interface TeacherIncident {
  kind: TeacherIncidentKind;
  enrollmentId: string;
  courseId?: string;
  milestoneSequence?: number;
  occurredAt: string;
  reference: string;
}

export interface TeacherOperationalProfile extends TeacherHealthSummary {
  verifiedAt?: string;
  application: {
    applicationId: string;
    state: string;
    submittedAt?: string;
    reviewedAt?: string;
  } | null;
  courses: Array<{
    courseId: string;
    title: string;
    status: string;
    discoverable: boolean;
    subject: string;
    gradeLevel: string[];
  }>;
  enrollments: Array<{ enrollmentId: string; courseId: string; enrollmentDate: string }>;
  incidents: { cancellations: number; noShows: number; history: TeacherIncident[] };
  probations: Array<{
    auditId: string;
    occurredAt: string;
    actorLabel: string;
    reason: string;
    parameterVersion?: string;
    objectLabel: string;
  }>;
}

export const getTeacherHealthList = (): Promise<{ teachers: TeacherHealthSummary[] }> =>
  apiGet('/api/v1/admin/teachers/health');

export const getTeacherProfile = (teacherId: string): Promise<TeacherOperationalProfile> =>
  apiGet(`/api/v1/admin/teachers/${encodeURIComponent(teacherId)}/health`);

export const recordProbation = (
  teacherId: string,
  reason: string,
): Promise<{ replayed: boolean }> =>
  apiPost(`/api/v1/admin/teachers/${encodeURIComponent(teacherId)}/probation`, { reason });

/* --------------------------------------------------------------- coverage */

export type SupplyClass = 'NO_SUPPLY' | 'AWAITING_DEMAND';

export interface SupplyCell {
  subject: string;
  grade: string;
  courses: number;
  teachers: number;
  courseIds: string[];
  supplyClass: SupplyClass;
}

export interface BlockedDimension {
  available: false;
  blockedBy: string;
  detail: string;
}

export interface SupplyCoverageView {
  subjects: string[];
  grades: string[];
  cells: SupplyCell[];
  outsideTaxonomy: string[];
  discoverableCourses: number;
  thresholds: { healthyFromPct: number; thinFromPct: number; version: string };
  demand: BlockedDimension;
  age: BlockedDimension;
  computedAt: string;
}

export const getSupplyCoverage = (): Promise<SupplyCoverageView> =>
  apiGet('/api/v1/admin/coverage/supply');
